import { useLocation, useNavigate } from "react-router-dom";
import {
  PayWithCash,
  PayWithGiftcard,
  PayWithLoyalty,
  PayWithSquare,
  PayWithStripe,
  CartUser,
  CartView,
  CartDiscount,
} from "../components";
import { AddTip } from "../components/AddTip";
import ConfirmModal from "../components/_modals/ConfirmModal";
import {
  useShipping,
  usePayment,
  useLoyalty,
  useOrder,
  useLayout,
} from "../context";
import { useStore, useAuth, useOrderOption, useCart } from "../hooks";
import { useEffect, useState, useMemo, useCallback } from "react";
import { formatCurrency } from "../utilities";
import { CheckoutApi, GetPaymentApi } from "../services";
import { GatewayEnum, OrderType } from "../enums";
import { OrderModel, PaymentModel } from "../models";
import { Layout } from "../layouts";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Col, Collapse, Row } from "react-bootstrap";
import CartPaymentSummary from "../components/cart/CartPaymentSummary";
import {
  Cart,
  ChevronDown,
  ChevronRight,
  Pencil,
  Tag,
  X,
} from "react-bootstrap-icons";
import { IoArrowBackSharp } from "react-icons/io5";
import { PiMapPinFill } from "react-icons/pi";
import { RiLockFill } from "react-icons/ri";
import Skeleton from "react-loading-skeleton";
import { BiLock } from "react-icons/bi";
import { CgDanger, CgNotes } from "react-icons/cg";
import React from "react";
import { PaymentGatewayModel } from "../models/PaymentGatewayModel";
import { Toast } from "../components/Toast";

const CheckoutPage = () => {
  let navigate = useNavigate();
  const location = useLocation();

  const { storeId, selectedStore, storeGateways, utm, loyaltyLevels } =
    useStore();
  const { cartId, cartQuantity, clearCart } = useCart();
  const { orderOption, setOrderOption, resetOrderOption } = useOrderOption();
  const { shippingLocation, setShippingLocation } = useShipping();
  const { loyaltyPoints, updateLoyalty } = useLoyalty();
  const {
    card,
    setCard,
    saveCard,
    savedCard,
    setMode,
    applePay,
    setApplePay,
    googlePay,
    setGooglePay,
    paymentBreakup,
    setPaymentBreakup,
    resetPayment,
  } = usePayment();
  const { setLastOrderId } = useOrder();
  const { customerId, customer, setCustomer, deviceId } = useAuth();

  const { setShowOptionModal } = useLayout();

  const stripe = useStripe();
  const elements = useElements();

  const [showCart, setShowCart] = useState<boolean>(false);
  const [showCartDiscount, setShowCartDiscount] = useState<boolean>(false);
  const [couponInput, setCouponInput] = useState<string>("");

  const [tip, setTip] = useState<string>(orderOption.tip);
  const [loyalty, setLoyalty] = useState(0);
  const [gateways, setGateways] = useState<PaymentGatewayModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [couponCode, setCouponCode] = useState<string>("");
  const [couponOpen, setCouponOpen] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState<boolean>(false);
  const [couponError, setCouponError] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const getGateway = useCallback(
    (id: GatewayEnum) =>
      storeGateways?.find((item) => item.gateway_id === id) ?? null,
    [storeGateways],
  );

  const {
    stripeGateway,
    squareGateway,
    cashGateway,
    giftcardGateway,
    loyaltyGateway,
  } = useMemo(
    () => ({
      stripeGateway: getGateway(GatewayEnum.STRIPE),
      squareGateway: getGateway(GatewayEnum.SQUAREUP),
      cashGateway: getGateway(GatewayEnum.CASH),
      giftcardGateway: getGateway(GatewayEnum.GIFTCARD),
      loyaltyGateway: getGateway(GatewayEnum.LOYALTY),
    }),
    [getGateway],
  );

  const activeLoyaltyLevels = useMemo(() => {
    return loyaltyLevels.filter((item) => item.points <= Number(loyaltyPoints));
  }, [loyaltyLevels, loyaltyPoints]);

  useEffect(() => {
    // if (!cartItems.length || !storeGateways.length) navigate("/menu");
    if (!storeGateways.length) navigate("/menu");

    setCard(undefined);
    setApplePay(undefined);
    setGooglePay(undefined);
    handleTipChange("10%");
  }, []);

  // useEffect(() => {
  //   if (activeLoyaltyLevels.length > 0) {
  //     setLoyalty(
  //       Number(activeLoyaltyLevels[activeLoyaltyLevels.length - 1].points),
  //     );
  //   }
  // }, [activeLoyaltyLevels]);

  useEffect(() => {
    if (storeGateways.length > 0) setIsPageLoading(false);
  }, [storeGateways]);

  useEffect(() => {
    if (storeGateways.length === 0 || gateways.length > 0) return;

    if (squareGateway) {
      setGateways([
        {
          gateway_id: squareGateway.gateway_id,
          gateway_name: squareGateway.gateway_name,
          mode: "card",
        },
      ]);
    } else if (stripeGateway) {
      setGateways([
        {
          gateway_id: stripeGateway.gateway_id,
          gateway_name: stripeGateway.gateway_name,
          mode: "credit",
        },
      ]);
    } else if (cashGateway) {
      setGateways([
        {
          gateway_id: cashGateway.gateway_id,
          gateway_name: cashGateway.gateway_name,
          mode: "cash",
        },
      ]);
    }
  }, [storeGateways]);

  useEffect(() => {
    if (gateways.length > 0) updatePayment();
  }, [gateways, tip, couponCode]);

  const handleGoBack = () => {
    // Check if the user has previous history in the browser state
    if (location.key !== "default") {
      navigate(-1); // Go to the previous page
    } else {
      navigate("/menu"); // Fallback route for direct page loads
    }
  };

  const handleTipChange = useCallback(
    (tip: string) => {
      setTip(tip);
      setOrderOption({ ...orderOption, tip });
    },
    [setTip, setOrderOption],
  );

  const handleOrderInstructionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setOrderOption({
        ...orderOption,
        order_instructions: e.target.value,
      });
    },
    [setOrderOption],
  );

  const checkout = async (updatedPayment: PaymentModel[]) => {
    try {
      setIsLoading(true);
      const { status, message, data } = await CheckoutApi({
        store_id: storeId,
        device_id: deviceId,
        devices: { id: "", type: "web" },
        customer_id: customerId,
        customer,
        cart_id: cartId,
        order_option: orderOption,
        address: {
          shipping: shippingLocation,
          billing: shippingLocation,
        },
        payment: updatedPayment,
        market_data: {
          olo_utm: utm,
          olo_ref: "",
        },
        scheme: "a2m",
      });

      if (status) {
        updateLoyalty();
        setLastOrderId(data.order_id);
        setCustomer({ ...customer, id: String(data.customer.id) });
        markConversion(data, orderOption.utm_source ?? "");
        resetCart();
        resetPayment();
        Toast.success(
          "Order Placed",
          `Your order #${data.order_id} has been sent to ${selectedStore?.store_name}.`,
        );
        navigate(`/order-confirmation/${data.order_id}`);
      } else {
        setErrorMessage(message);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMessage("An error occurred during checkout.");
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const markConversion = (order: OrderModel, utmSource: string) => {
    if (utmSource === "#/adsgl" || utmSource.includes("adsgl")) {
      const dl = (window as any).dataLayer;
      if (!Array.isArray(dl)) return;
      dl.push({
        event: "purchase",
        currency: "USD",
        transaction_id: order.order_id,
        order_id: order.order_id,
        order_value: order.order_total,
        store_id: order.store_id,
      });
    }
  };

  const resetCart = () => {
    clearCart();
    setShippingLocation([]);
    resetOrderOption();
    setCard(null);
  };

  const updatePayment = async () => {
    try {
      setIsLoading(true);
      const { status, message, data } = await GetPaymentApi({
        store_id: storeId,
        device_id: deviceId,
        devices: { id: deviceId, type: "web" },
        customer_id: customerId,
        cart_id: cartId,
        order_option: orderOption,
        coupon_code: couponCode,
        shipping: shippingLocation,
        payment: gateways,
      });

      if (status) {
        setPaymentBreakup(data.total);
        setShowCartDiscount(false);
      } else {
        setCouponCode("");
        setCouponError(message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentModeChange = useCallback(
    (gatewayId: GatewayEnum, mode: string = "", isChecked: boolean = true) => {
      const exclusiveGateways = storeGateways
        .filter((item) => item.is_multiple == "0")
        .map((item) => item.gateway_id);

      // Update Mode
      if (exclusiveGateways.includes(gatewayId) || mode === "savedcard")
        setMode(mode);

      // Update Gateways
      setGateways((prevGateways) => {
        if (isChecked) {
          if (exclusiveGateways.includes(gatewayId)) {
            // Replace any existing exclusive gateways with the new one
            const filteredGateways = prevGateways.filter(
              (gateway) => !exclusiveGateways.includes(gateway.gateway_id),
            );
            return [
              ...filteredGateways,
              {
                gateway_id: gatewayId,
                gateway_name: getGateway(gatewayId)?.gateway_name,
                mode: mode,
              },
            ];
          }
          // Add the gatewayTypeId if it's not exclusive
          return [
            ...prevGateways,
            {
              gateway_id: gatewayId,
              gateway_name: getGateway(gatewayId)?.gateway_name,
              mode: mode,
              ...(gatewayId === GatewayEnum.LOYALTY && { points: loyalty }),
            },
          ];
        }
        // Remove the gatewayTypeId if unchecked
        return prevGateways.filter(
          (gateway) => gateway.gateway_id !== gatewayId,
        );
      });
    },
    [storeGateways, getGateway, loyalty],
  );

  const handleBtnClick = async () => {
    const selectedPayments: PaymentModel[] = gateways.map((gateway) => ({
      gateway_id: gateway.gateway_id,
      gateway_name: getGateway(gateway.gateway_id)?.gateway_name,
      mode: gateway.mode,
      amount: (gateway.gateway_id === GatewayEnum.GIFTCARD
        ? paymentBreakup.giftcard
        : gateway.gateway_id === GatewayEnum.LOYALTY
          ? paymentBreakup.loyalty
          : paymentBreakup.order_total
      ).toString(),
      ...(gateway.gateway_id === GatewayEnum.LOYALTY && {
        points: loyalty,
      }),
      ...(gateway.gateway_id === GatewayEnum.GIFTCARD && {
        code: gateway.code,
      }),
    }));

    let paymentGateways = gateways.map((item) => item.gateway_id);
    let paymentModes = gateways.map((item) => item.mode.toLowerCase());

    if (paymentGateways.includes(GatewayEnum.SQUAREUP)) {
      if (paymentModes.includes("card"))
        await handleSquarePayment(selectedPayments);
      else if (paymentModes.includes("applepay"))
        await handleApplePayment(selectedPayments);
      else if (paymentModes.includes("googlepay"))
        await handleGooglePayment(selectedPayments);
      else if (paymentModes.includes("savedcard"))
        await handleSavedCardPayment(selectedPayments);
    } else if (paymentGateways.includes(GatewayEnum.STRIPE)) {
      await handleStripePayment(selectedPayments);
    } else {
      await checkout(selectedPayments);
    }
  };

  const handleSquarePayment = async (payment: PaymentModel[]) => {
    try {
      const tokenResult = await card?.tokenize();
      if (tokenResult?.status === "OK") {
        const updatedPayments = payment.map((item) =>
          item.gateway_id === GatewayEnum.SQUAREUP
            ? {
                ...item,
                mode: "credit",
                card: {
                  cc_token: tokenResult.token,
                  brand: tokenResult.details.card.brand,
                  save_card: saveCard,
                },
              }
            : item,
        );
        checkout(updatedPayments);
      } else {
        console.error("Payment tokenization failed:", tokenResult?.errors);
        setErrorMessage(tokenResult?.errors[0].message);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error during payment tokenization:", error);
      setErrorMessage("Error during payment tokenization");
      setShowErrorModal(true);
    }
  };

  const handleGooglePayment = async (payment: PaymentModel[]) => {
    try {
      const tokenResult = await googlePay?.tokenize();
      if (tokenResult?.status === "OK") {
        const updatedPayments = payment.map((item) =>
          item.mode === "googlepay"
            ? {
                ...item,
                mode: "credit",
                card: {
                  cc_token: tokenResult.token,
                },
              }
            : item,
        );
        checkout(updatedPayments);
      } else {
        if (tokenResult && tokenResult?.status != "Cancel") {
          let errorMessage = `Tokenization failed with status: ${tokenResult?.status}`;
          console.error("Payment tokenization failed:", errorMessage);
          setErrorMessage(errorMessage);
          setShowErrorModal(true);
        }
      }
    } catch (error) {
      console.error("Error during payment tokenization:", error);
      setErrorMessage("Error during payment tokenization");
      setShowErrorModal(true);
    }
  };

  const handleApplePayment = async (payment: PaymentModel[]) => {
    try {
      const tokenResult = await applePay?.tokenize();
      if (tokenResult?.status === "OK") {
        const updatedPayments = payment.map((item) =>
          item.mode === "applepay"
            ? {
                ...item,
                mode: "credit",
                card: {
                  cc_token: tokenResult.token,
                },
              }
            : item,
        );
        checkout(updatedPayments);
      } else {
        if (tokenResult && tokenResult?.status != "Cancel") {
          let errorMessage = `Tokenization failed with status: ${tokenResult?.status}`;
          console.error("Payment tokenization failed:", errorMessage);
          setErrorMessage(errorMessage);
          setShowErrorModal(true);
        }
      }
    } catch (error) {
      console.error("Error during payment tokenization:", error);
      setErrorMessage("Error during payment tokenization");
      setShowErrorModal(true);
    }
  };

  const handleSavedCardPayment = (payment: PaymentModel[]) => {
    if (!savedCard?.cvv) {
      Toast.error("Please enter CVV");
      return;
    }

    const updatedPayments = payment.map((item) =>
      item.mode === "savedcard"
        ? {
            ...item,
            mode: "credit",
            card: {
              cc_id: savedCard?.token,
              cc_token: "",
            },
          }
        : item,
    );

    checkout(updatedPayments);
  };

  const handleStripePayment = async (payment: PaymentModel[]) => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      console.warn("Stripe.js has not loaded yet.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      console.warn("Card information is not available.");
      return;
    }

    const { token, error } = await stripe.createToken(cardElement);
    if (error) {
      Toast.error(error.message || "An unexpected error occurred.");
    }

    const updatedPayments = payment.map((item) =>
      item.gateway_id === GatewayEnum.STRIPE
        ? {
            ...item,
            card: {
              cc_token: token?.id,
              save_card: saveCard,
            },
          }
        : item,
    );
    checkout(updatedPayments);
  };

  const handleApplyCoupon = async (couponCode: string) => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }
    setCouponError("");
    setCouponCode(couponCode);
  };

  const isCouponApplied = Boolean(couponCode) && paymentBreakup.discount > 0;

  const isGatewaySelected = useCallback(
    (gatewayId: GatewayEnum) => gateways.some((g) => g.gateway_id == gatewayId),
    [gateways],
  );

  const handleLoyaltyPointsChange = (value: string) => {
    const points = Number(value);
    setLoyalty(points);

    setGateways((prev) => {
      if (points === 0) {
        return prev.filter((g) => g.gateway_id !== GatewayEnum.LOYALTY);
      }

      const exists = prev.some((g) => g.gateway_id === GatewayEnum.LOYALTY);
      if (exists) {
        return prev.map((g) =>
          g.gateway_id === GatewayEnum.LOYALTY ? { ...g, points } : g,
        );
      }

      return [
        ...prev,
        {
          gateway_id: GatewayEnum.LOYALTY,
          gateway_name: loyaltyGateway?.gateway_name,
          mode: "loyalty",
          points,
        },
      ];
    });
  };

  const handleGiftcardCode = (code: string) => {
    setGateways((prev) => {
      if (!code) {
        return prev.filter((g) => g.gateway_id !== GatewayEnum.GIFTCARD);
      }
      const exists = prev.some((g) => g.gateway_id === GatewayEnum.GIFTCARD);
      if (exists) {
        return prev.map((g) =>
          g.gateway_id === GatewayEnum.GIFTCARD ? { ...g, code } : g,
        );
      }
      return [
        ...prev,
        {
          gateway_id: GatewayEnum.GIFTCARD,
          gateway_name: giftcardGateway?.gateway_name,
          mode: "giftcard",
          code,
        },
      ];
    });
  };

  return (
    <Layout showLoader={isLoading} showCartIcon={false}>
      {isPageLoading && (
        // <Loader />
        <Row style={{ width: "80%", margin: "auto" }} className="my-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
            <React.Fragment key={item}>
              <Col sm={12} md={6} key={item}>
                <Skeleton height={100} className="mb-3" />
              </Col>
              <Col>
                <Skeleton height={100} className="mb-3" />
              </Col>
            </React.Fragment>
          ))}
        </Row>
      )}

      <div className="row d-flex justify-content-around checkout-page-width">
        <div className="col-md-12 col-lg-7 col-sm-12 checkout-details-container">
          <Row className="checkout-page-pd">
            <div className="d-flex justify-content-between align-items-center w-100 mb-2 checkout-sticky-header">
              <h2 className="page-heading d-flex align-items-center gap-2 mb-0">
                <span className="cursor-pointer" onClick={handleGoBack}>
                  {" "}
                  <IoArrowBackSharp />{" "}
                </span>{" "}
                Checkout
              </h2>
              <div className="checkout-secure-text">
                <RiLockFill size={18} /> <span> Secure Checkout </span>
              </div>
            </div>

            <Col xs={12} md={12} className="pd-lr-md-40">
              <div className="choose_payment_method_main">
                <div className="border-box-checkout mb-2">
                  <h2>
                    <span className="mx-1">
                      {orderOption.type_id == OrderType.DELIVERY
                        ? "Delivery"
                        : orderOption.type_id == OrderType.PICKUP
                          ? "Pickup"
                          : orderOption.type_id == OrderType.DINEIN
                            ? "Dine In"
                            : orderOption.type_id == OrderType.GIFTCARD
                              ? "Giftcard"
                              : ""}
                    </span>
                    Details
                  </h2>
                  <div className="border-card">
                    <CartUser className="mb-3" />

                    {/* Order Options */}
                    <div className="row my-2 g-0">
                      {/* <div className="col-12 p-0 d-flex justify-content-between align-items-center mb-3">
                      <h2 className="mb-0">Order Options</h2>
                    </div> */}

                      {/* Address Section */}
                      <div className="col-12 cart_section_select">
                        <div
                          className="d-flex align-items-start py-1"
                          style={{ padding: "0px 20px" }}
                        >
                          <PiMapPinFill className="icon-style me-2" size={22} />

                          {orderOption.type_id == OrderType.DELIVERY ? (
                            <div className="flex-grow-1 checkout-address">
                              <h5 className="mb-1">Deliver to</h5>
                              <p className="mb-0">
                                {shippingLocation?.formatted_address}
                              </p>
                            </div>
                          ) : orderOption.type_id == OrderType.PICKUP ? (
                            <div className="flex-grow-1 checkout-address">
                              <h5 className="mb-1">Pickup from</h5>
                              <p>{selectedStore?.store_name}</p>
                              <p className="mb-0">
                                {selectedStore?.store_address}
                              </p>
                            </div>
                          ) : orderOption.type_id == OrderType.DINEIN ? (
                            <div className="flex-grow-1 checkout-address">
                              <h5 className="mb-1">Dine In at</h5>
                              <p className="mb-0">
                                {selectedStore?.store_address}
                              </p>
                            </div>
                          ) : orderOption.type_id == OrderType.GIFTCARD ? (
                            <div className="flex-grow-1 checkout-address">
                              <h5 className="mb-1">Recipient</h5>
                              <p className="mb-0">
                                {shippingLocation?.first_name}{" "}
                                {shippingLocation?.last_name},{" "}
                                {shippingLocation?.address_street},{" "}
                                {shippingLocation?.city_name},{" "}
                                {shippingLocation?.state_code},{" "}
                                {shippingLocation?.zip_code}
                              </p>
                            </div>
                          ) : (
                            ""
                          )}

                          <button
                            onClick={() => setShowOptionModal(true)}
                            className="d-none border-0 edit-user-btn d-flex justify-content-center align-items-center"
                          >
                            <Pencil />
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* End */}
                  </div>
                </div>

                <div className="payment-method-section">
                  <div className="payment-method-header">
                    {/* <div className="pd-lr-md-40"> */}
                    <h2>Payment Method</h2>
                    {/* </div> */}
                    <div className="secure-text">
                      <RiLockFill />{" "}
                      <span> Your Payment is secure and encrypted </span>
                    </div>
                  </div>

                  <div className="border-box-checkout pt-3">
                    {(giftcardGateway || loyaltyGateway) && (
                      <div
                        className={`choose_payment_method_colm card mb-2 ${
                          isGatewaySelected(GatewayEnum.GIFTCARD) ||
                          isGatewaySelected(GatewayEnum.LOYALTY)
                            ? "payment-method-active"
                            : ""
                        }`}
                      >
                        {giftcardGateway && (
                          <PayWithGiftcard
                            storeId={storeId}
                            isDisabled={isGatewaySelected(GatewayEnum.LOYALTY)}
                            onApply={handleGiftcardCode}
                          />
                        )}

                        {loyaltyGateway && (
                          <PayWithLoyalty
                            loyaltyLevels={activeLoyaltyLevels}
                            loyaltyPointsUsed={String(loyalty)}
                            onSelectChange={handleLoyaltyPointsChange}
                            isDisabled={isGatewaySelected(GatewayEnum.GIFTCARD)}
                          />
                        )}
                      </div>
                    )}
                    {squareGateway && (
                      <PayWithSquare
                        total={paymentBreakup.order_total}
                        gateway={squareGateway}
                        handlePaymentModeChange={handlePaymentModeChange}
                        isChecked={isGatewaySelected(squareGateway.gateway_id)}
                      />
                    )}
                    {stripeGateway && (
                      <PayWithStripe
                        gateway={stripeGateway}
                        handlePaymentModeChange={handlePaymentModeChange}
                        isChecked={isGatewaySelected(stripeGateway.gateway_id)}
                      />
                    )}
                    {cashGateway && (
                      <PayWithCash
                        handlePaymentModeChange={handlePaymentModeChange}
                        isChecked={isGatewaySelected(cashGateway.gateway_id)}
                      />
                    )}
                    {/* SECURITY TEXT */}
                    <div className="auth-security-note my-4">
                      <BiLock size={18} className="me-2" />
                      We keep your information secure and private
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div className="col-md-12 col-lg-5 col-sm-12 checkout-payment-cart">
          <div className="checkout-cart-section">
            <h3 className="mb-3 checkout-heading">
              Order Summary ({cartQuantity}{" "}
              {cartQuantity === 1 ? "item" : "items"})
            </h3>
            <div className="border-bottom-gray p-0"></div>
            <div
              onClick={() => setShowCart(true)}
              className="cursor-pointer col-md-12 d-flex justify-content-between align-item-center cart_section_select py-3"
            >
              <div className="cart_icon d-flex justify-content-start align-item-center">
                <Cart className="icon-style" />
                <h2 className="cart_heading px-2">
                  Cart Summary ({cartQuantity} Item)
                </h2>
              </div>
              <div className="content">
                <ChevronRight />
              </div>
            </div>
            <div className="border-bottom-gray p-0"></div>

            <div className="checkout-coupon-section flex-column">
              <div
                className="d-flex justify-content-between align-items-center cursor-pointer coupon-toggle-row"
                onClick={() => setCouponOpen(!couponOpen)}
              >
                <div className="d-flex align-items-center gap-2 cart_section_select">
                  <Tag size={22} className="icon-style" />
                  <span
                    className={`coupon-label ${isCouponApplied ? "cart_heading apply-coupon" : ""}`}
                  >
                    {isCouponApplied ? (
                      <span className="text-success">
                        Coupon Applied ({couponCode})
                      </span>
                    ) : (
                      "Add Promo Code"
                    )}
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  {isCouponApplied && (
                    <button
                      type="button"
                      className="coupon-remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCouponCode("");
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}

                  {!isCouponApplied && (
                    <ChevronDown
                      size={18}
                      className={`content ${couponOpen ? "coupon-chevron--open" : ""}`}
                    />
                  )}
                </div>
              </div>

              <Collapse in={couponOpen && !isCouponApplied} className="mx-3">
                <div>
                  <div className="d-flex align-items-center w-100 mt-2">
                    <input
                      type="text"
                      className="form-control coupon-input text-uppercase"
                      placeholder="Enter coupon code"
                      value={couponInput}
                      onChange={(e) => {
                        setCouponInput(e.target.value);
                        setCouponError("");
                      }}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleApplyCoupon(couponInput)
                      }
                    />
                    <button
                      type="button"
                      className="apply-coupon-btn"
                      onClick={() => handleApplyCoupon(couponInput)}
                    >
                      Apply
                    </button>
                  </div>

                  {couponError && (
                    <div className="d-flex align-items-center gap-1 mt-1">
                      <CgDanger size={18} className="error-icon" />
                      <span className="error-msg">{couponError}</span>
                    </div>
                  )}

                  <div className="d-flex justify-content-end mt-2">
                    <span
                      className="cursor-pointer view-coupons-link text-decoration-underline"
                      onClick={() => setShowCartDiscount(true)}
                    >
                      View Coupons
                    </span>
                  </div>
                </div>
              </Collapse>
            </div>
            {/* End */}

            <div className="border-bottom-gray p-0 mb-2"></div>

            <div className="form-group pb-0 mb-3">
              <div
                className="tip-toggle-row cursor-pointer"
                onClick={() => setInstructionsOpen(!instructionsOpen)}
              >
                <div className="d-flex align-items-center gap-2">
                  <span className="cart_section_select">
                    <CgNotes size={19} className="icon-style" />
                  </span>
                  <div>
                    <h2 className="coupon-label mb-0">
                      Add Special Instructions
                    </h2>
                  </div>
                </div>
                <ChevronDown
                  size={16}
                  className={`tip-chevron ${instructionsOpen ? "tip-chevron--open" : ""}`}
                />
              </div>

              <Collapse in={instructionsOpen} className="mx-3">
                <div>
                  <textarea
                    className="form-control mt-2"
                    onInput={handleOrderInstructionChange}
                    value={orderOption.order_instructions}
                    placeholder="Add instructions for your order"
                  />
                </div>
              </Collapse>
            </div>

            <div className="border-bottom-gray p-0 mb-3"></div>

            {orderOption.tip_status === "1" && (
              <AddTip
                defaultValue={tip}
                orderTypeId={orderOption.type_id}
                handleChange={handleTipChange}
              />
            )}

            <div className="choose_payment_subtotal common-card-border">
              {/* <p className="mb-2 checkout-heading">Payment Summary</p> */}
              <CartPaymentSummary
                paymentBreakup={paymentBreakup}
                couponCode={couponCode}
                gateways={gateways}
                orderOption={orderOption}
              />
            </div>
            <div className="choose_payment_subtotal checkout-cart-fixed-btn px-0">
              <button
                className="mb-0"
                onClick={handleBtnClick}
                disabled={isLoading}
              >
                {(paymentBreakup.to_pay || 0) > 0 ? (
                  <>
                    <RiLockFill size={18} />
                    <span className="mx-1">{`PAY ${formatCurrency(paymentBreakup.order_total)}`}</span>
                  </>
                ) : (
                  "Place Order"
                )}
              </button>
            </div>
            {/* <div>
              <span className="auth-security-note mb-3"> You won't be charged until confirmation </span>
            </div> */}
          </div>
        </div>
      </div>

      <CartView show={showCart} handleClose={() => setShowCart(false)} />
      <CartDiscount
        show={showCartDiscount}
        handleClose={() => setShowCartDiscount(false)}
        couponCode={couponCode}
        validCoupon={isCouponApplied}
        handleApplyCoupon={handleApplyCoupon}
      />

      <ConfirmModal
        show={showErrorModal}
        title="Error"
        content={errorMessage}
        btnText="Dismiss"
        variant="danger"
        handleSubmit={() => setShowErrorModal(false)}
        handleClose={() => setShowErrorModal(false)}
      />
    </Layout>
  );
};

export default CheckoutPage;
